import {NgForOf, NgIf} from '@angular/common';
import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {TranslateModule} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {
  ATTACHMENTS_ACTIONS,
  AttachmentType,
  BaseDialogComponent,
  ConfigService,
  handleError,
  OrgModelLinkNode,
  Q9HtmlDecoderPipe,
  Q9HtmlEncoderPipe,
  Refmodel
} from '@q9elements/ui-kit/common';
import {
  ContentLoaderComponent,
  DialogCloseButtonComponent,
  GroupsSelectComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  TagOption,
  TextareaFieldComponent,
  TextFieldComponent,
  InfoMessageComponent
} from '@q9elements/ui-kit/components';

import {ModeDataService} from '../../../../editor-state/mode-data.service';
import {LinkedSFNodesService} from '../../../services/linked-sf-nodes.service';
import {MatExpansionModule} from '@angular/material/expansion';
import {SfNodeInfoComponent} from './sf-node-info/sf-node-info.component';
import {
  AdditionalLinkInfo,
  AttachOrgNodeLinkDialogComponentData,
  AttachOrgNodeLinkDialogResult,
  SfRefModelNode
} from './models/org-node-link.interface';

@Component({
  selector: 'app-attach-org-node-link-dialog',
  templateUrl: './attach-org-node-link-dialog.component.html',
  styleUrls: ['./attach-org-node-link-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    TranslateModule,
    ReactiveFormsModule,
    NgIf,
    PrimaryButtonComponent,
    SecondaryButtonComponent,
    DialogCloseButtonComponent,
    TextareaFieldComponent,
    GroupsSelectComponent,
    TextFieldComponent,
    Q9HtmlDecoderPipe,
    NgForOf,
    ContentLoaderComponent,
    MatExpansionModule,
    SfNodeInfoComponent,
    InfoMessageComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachOrgNodeLinkDialogComponent extends BaseDialogComponent implements OnInit {
  public sfnLinkAttachmentForm: UntypedFormGroup;
  public sfn: any;
  public loading = signal(false);
  public isPreview = false;

  private sfnLink: string;
  private additionalInfo: AdditionalLinkInfo;

  protected readonly ATTACHMENTS_ACTIONS = ATTACHMENTS_ACTIONS;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: AttachOrgNodeLinkDialogComponentData,
    public dialogRef: MatDialogRef<AttachOrgNodeLinkDialogComponent>,
    private modeDataSvc: ModeDataService,
    private linkedSFNodesService: LinkedSFNodesService,
    private encodeHtmlPipe: Q9HtmlEncoderPipe
  ) {
    super();
  }

  ngOnInit() {
    this.isPreview = !this.modeDataSvc.isEditMode();
    this.getSFNDetails();
  }

  public openSFN() {
    window.open(this.sfnLink);
  }

  protected override sendSubmitRequest(): Observable<AttachOrgNodeLinkDialogResult> {
    const data = this.updateSFNLink();

    return this.data.onSubmit(data);
  }

  private getSFNDetails(): void {
    this.loading.set(true);
    const {isEdit, attachment} = this.data;

    this.sfn = {
      ...attachment,
      sfNode: isEdit ? attachment.sfNode : {_id: attachment.sfNode}
    };

    this.linkedSFNodesService
      .getLinkedSFNode(this.sfn.sfNode._id)
      .pipe(
        this.takeUntilComponentDestroy(),
        handleError(),
        tap((deInfo: OrgModelLinkNode) => {
          if (deInfo) {
            this.setAdditionalInfo(deInfo);
          }
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe();
  }

  private getSFNLink(refModelNodes: SfRefModelNode[]) {
    const [node] = refModelNodes;
    const refModelId = (node.refModel as Partial<Refmodel>)._id;

    this.sfnLink =
      ConfigService.environment.REF_MODEL_APP + `/refmodel/${refModelId}?node=${node._id}`;
  }

  private setAdditionalInfo(deInfo: OrgModelLinkNode) {
    if (deInfo.refModelNodes) {
      const nodesWithLinks = deInfo.refModelNodes.map(refModelNode => {
        const refModelId = (refModelNode.refModel as Partial<Refmodel>)._id;

        return {
          ...refModelNode,
          appLink:
            ConfigService.environment.REF_MODEL_APP +
            `/refmodel/${refModelId}?node=${refModelNode._id}`
        };
      });

      this.sfn.sfNode['refModelNodes'] = [...nodesWithLinks];
      this.sfn.sfNode['type'] = deInfo.type;

      const {linkTitle, nodeApiName, nodeDescription, nodeObjectParent, objectType, nodeSummary} =
        deInfo;

      this.additionalInfo = {
        linkTitle,
        nodeApiName,
        nodeDescription,
        nodeObjectParent,
        objectType,
        nodeSummary
      };

      this.getSFNLink(this.sfn.sfNode.refModelNodes);
    }

    this.createForm();
  }

  private createForm() {
    const sfnName = this.data.isEdit ? this.sfn.title : this.sfn.name;

    this.sfnLinkAttachmentForm = new FormGroup({
      description: new FormControl(
        {
          value: this.decode(this.sfn.description) || '',
          disabled: this.isPreview
        },
        {
          nonNullable: true,
          validators: Q9Validators.maxLength(32000)
        }
      ),
      title: new FormControl(
        {
          value: this.decode(sfnName),
          disabled: this.isPreview
        },
        {
          nonNullable: true,
          validators: [
            Q9Validators.minLength(2),
            Q9Validators.maxLength(150),
            Q9Validators.required()
          ]
        }
      ),
      groups: new FormControl<TagOption[] | string[]>(
        {value: this.sfn.groups, disabled: this.isPreview},
        {
          nonNullable: true
        }
      )
    });
  }

  private decode(string = '') {
    return this.encodeHtmlPipe.transform(string, true);
  }

  private updateSFNLink(): AttachOrgNodeLinkDialogResult {
    const {
      nodeDescription,
      nodeObjectParent,
      objectType,
      nodeSummary,
      nodeApiName,
      linkTitle: linkDisplayName
    } = this.additionalInfo;

    const {sfNode, nodePath} = this.sfn;

    return this.data.isEdit
      ? {
          ...this.sfnLinkAttachmentForm.getRawValue(),
          mxKey: this.sfn.mxKey,
          sfNode: true
        }
      : {
          ...this.sfnLinkAttachmentForm.getRawValue(),
          type: AttachmentType.ORG_MODEL,
          sfNode,
          nodePath,
          nodeDescription,
          nodeSummary,
          nodeApiName,
          nodeObjectParent,
          objectType,
          linkDisplayName
        };
  }
}
